import React from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { NotificationsBell } from './NotificationsBell';
import { 
    LayoutDashboard, 
    Building2, 
    FileText, 
    // ArrowLeftRight,  // legacy Reconciliation nav (hidden — superseded by Bank matching)
    Calculator,
    FileSpreadsheet,
    CreditCard,
    Users,
    Settings,
    LogOut,
    ChevronDown,
    ChevronRight,
    UsersRound,
    Brain,
    Shield,
    Bell,
    Clock,
    FolderOpen,
    Banknote,
    GitCompareArrows,
    X
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import { Button } from '../ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import { cn } from '../../lib/utils';

// Portfolio Risk Platform - Primary Navigation
const portfolioItems = [
    { to: '/app/portfolio', icon: Shield, label: 'Risk Dashboard' },
    { to: '/app/portfolio/exceptions', icon: Bell, label: 'Exception Inbox' },
    { to: '/app/portfolio/pre-vat', icon: Clock, label: 'Pre-VAT Review' },
];

// Legacy/Secondary Navigation
const navTop = [
    { to: '/app/dashboard', icon: LayoutDashboard, label: 'Overview' },
    { to: '/app/companies', icon: Building2, label: 'Companies' },
];

// Operational client-workspace pages — grouped under one collapsible category (collapsed by
// default) so they aren't loose in the nav / exposed on a shared screen. Fully functional.
const bookkeepingItems = [
    { to: '/app/documents', icon: FileText, label: 'Bilag' },
    // Vouchers page retired from the accountant UI after the delivery consolidation
    // (vouchers->push path is dead; e-conomic delivery lives on the Bank export page).
    // Voucher records are still created harmlessly in the background.
    { to: '/app/bank', icon: Banknote, label: 'Bank statements' },
    { to: '/app/bank/matching', icon: GitCompareArrows, label: 'Bank matching' },
    { to: '/app/bank/coding', icon: Calculator, label: 'Bank coding' },
    { to: '/app/bank/export', icon: FileSpreadsheet, label: 'Bank export' },
    { to: '/app/vat', icon: Calculator, label: 'VAT Analysis' },
];

const navBottom = [
    { to: '/app/activity', icon: Users, label: 'Activity Log' },
];

// Customer billing/plan chart — ADMIN ONLY. Prices aren't final; firm accountants
// (firm_admin/firm_staff) must not see the package/price chart. Admin manages pricing
// per-firm under "Firms & Billing" and retains access to this page.
const adminBillingItem = { to: '/app/billing', icon: CreditCard, label: 'Billing' };

const accountantItems = [
    { to: '/app/accountant', icon: UsersRound, label: 'Client Overview' },
    { to: '/app/ai-dashboard', icon: Brain, label: 'AI Performance' },
];

const adminItems = [
    { to: '/app/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/app/admin/firms', icon: Building2, label: 'Firms & Billing' },
    { to: '/app/admin/activity', icon: Clock, label: 'Activity & Audit' },
    { to: '/app/admin', icon: Users, label: 'Admin Panel' },
    { to: '/app/ai-dashboard', icon: Brain, label: 'AI Dashboard' },
];

export const Sidebar = ({ open = false, onClose = () => {} }) => {
    const { user, logout, isAccountant, isAdmin, isFirmOwner } = useAuth();
    const { tenants, currentTenant, selectTenant } = useTenant();
    const navigate = useNavigate();
    const location = useLocation();
    // Bookkeeping group: collapsed by default; auto-open only when already on one of its pages.
    const [bkOpen, setBkOpen] = React.useState(
        bookkeepingItems.some((i) => location.pathname === i.to || location.pathname.startsWith(i.to + '/')));

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    return (
        <>
            {/* Mobile backdrop — tap to close the off-canvas drawer */}
            {open && (
                <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={onClose} aria-hidden="true" />
            )}
            {/* Off-canvas on mobile (slides in), fixed/visible on lg+ */}
            <aside
                className={`fixed left-0 top-0 h-screen w-64 border-r border-border/60 bg-card/50 backdrop-blur-xl flex flex-col z-50 transform transition-transform duration-200 lg:translate-x-0 ${open ? 'translate-x-0' : '-translate-x-full'}`}
                data-testid="sidebar"
            >
            {/* Logo */}
            <div className="p-6 border-b border-border/60 flex items-start justify-between">
                <div>
                    <h1 className="font-heading text-xl font-bold text-primary">
                        AI Copilot
                    </h1>
                    <p className="text-xs text-muted-foreground mt-1">Accounting Assistant</p>
                </div>
                <div className="flex items-center gap-1">
                    {isAccountant && <NotificationsBell />}
                    {/* Close button — mobile only */}
                    <button onClick={onClose} className="lg:hidden p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted" aria-label="Close menu">
                        <X className="h-5 w-5" />
                    </button>
                </div>
            </div>

            {/* Tenant Selector */}
            {tenants.length > 0 && (
                <div className="p-4 border-b border-border/60">
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="outline" className="w-full justify-between" data-testid="tenant-selector">
                                <span className="truncate">{currentTenant?.name || 'Select Company'}</span>
                                <ChevronDown className="h-4 w-4 ml-2 shrink-0 opacity-50" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="w-56" align="start">
                            {tenants.map((tenant) => (
                                <DropdownMenuItem
                                    key={tenant.id}
                                    onClick={() => selectTenant(tenant)}
                                    className={cn(
                                        "cursor-pointer",
                                        currentTenant?.id === tenant.id && "bg-primary/10"
                                    )}
                                    data-testid={`tenant-option-${tenant.id}`}
                                >
                                    <Building2 className="h-4 w-4 mr-2" />
                                    {tenant.name}
                                </DropdownMenuItem>
                            ))}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => navigate('/app/companies')} className="cursor-pointer">
                                <Settings className="h-4 w-4 mr-2" />
                                Manage Companies
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            )}

            {/* Navigation */}
            <nav className="flex-1 overflow-y-auto p-4 space-y-1">
                {/* Secondary Navigation */}
                <div className="pt-4 pb-2">
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-3">
                        Tools
                    </span>
                </div>
                {navTop.map((item) => (
                    <NavLink
                        key={item.to}
                        to={item.to}
                        className={({ isActive }) => cn("sidebar-link", isActive && "active")}
                        data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                    >
                        <item.icon className="h-5 w-5" />
                        <span>{item.label}</span>
                    </NavLink>
                ))}

                {/* Bookkeeping — collapsible group (collapsed by default) */}
                <button
                    type="button"
                    onClick={() => setBkOpen((v) => !v)}
                    className="sidebar-link w-full justify-between"
                    data-testid="nav-bookkeeping-toggle"
                    aria-expanded={bkOpen}
                >
                    <span className="flex items-center gap-3"><FileSpreadsheet className="h-5 w-5" /><span>Bookkeeping</span></span>
                    {bkOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </button>
                {bkOpen && (
                    <div className="ml-3 pl-2 border-l border-border/60 space-y-1">
                        {bookkeepingItems.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.to === '/app/bank'}
                                className={({ isActive }) => cn("sidebar-link", isActive && "active")}
                                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                            >
                                <item.icon className="h-5 w-5" />
                                <span>{item.label}</span>
                            </NavLink>
                        ))}
                    </div>
                )}

                {navBottom.map((item) => (
                    <NavLink
                        key={item.to}
                        to={item.to}
                        className={({ isActive }) => cn("sidebar-link", isActive && "active")}
                        data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                    >
                        <item.icon className="h-5 w-5" />
                        <span>{item.label}</span>
                    </NavLink>
                ))}

                {/* Billing/plan chart — admin only (firm accountants must not see prices). */}
                {isAdmin && (
                    <NavLink
                        to={adminBillingItem.to}
                        className={({ isActive }) => cn("sidebar-link", isActive && "active")}
                        data-testid="nav-billing"
                    >
                        <adminBillingItem.icon className="h-5 w-5" />
                        <span>{adminBillingItem.label}</span>
                    </NavLink>
                )}

                {/* Portfolio Risk Platform — placed BELOW the working sections deliberately.
                    Nothing computes risk, exceptions or VAT deadlines from real client data yet;
                    these three screens were populated entirely by demo fixtures, which have been
                    removed. They stay in the nav, last and labelled, rather than leading it. */}
                <div className="pt-4 pb-2">
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-3">
                        Portfolio Control (coming soon)
                    </span>
                </div>
                {portfolioItems.map((item) => (
                    <NavLink
                        key={item.to}
                        to={item.to}
                        className={({ isActive }) => cn("sidebar-link", isActive && "active")}
                        data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                    >
                        <item.icon className="h-5 w-5 opacity-60" />
                        <span className="opacity-70">{item.label}</span>
                    </NavLink>
                ))}

                {/* Files = non-invoice "other" documents — accountant-only (firm owner + admin),
                    NOT staff. Backend also 403s staff on the Files documents. */}
                {isFirmOwner && (
                    <NavLink
                        to="/app/files"
                        className={({ isActive }) => cn("sidebar-link", isActive && "active")}
                        data-testid="nav-files"
                    >
                        <FolderOpen className="h-5 w-5" />
                        <span>Files</span>
                    </NavLink>
                )}

                {isAccountant && (
                    <>
                        <div className="pt-4 pb-2">
                            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-3">
                                Accountant
                            </span>
                        </div>
                        {accountantItems.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                className={({ isActive }) => cn("sidebar-link", isActive && "active")}
                                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                            >
                                <item.icon className="h-5 w-5" />
                                <span>{item.label}</span>
                            </NavLink>
                        ))}
                        {isFirmOwner && (
                            <NavLink
                                to="/app/staff"
                                className={({ isActive }) => cn("sidebar-link", isActive && "active")}
                                data-testid="nav-staff"
                            >
                                <UsersRound className="h-5 w-5" />
                                <span>Staff</span>
                            </NavLink>
                        )}
                    </>
                )}

                {isAdmin && (
                    <>
                        <div className="pt-4 pb-2">
                            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-3">
                                Admin
                            </span>
                        </div>
                        {adminItems.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                className={({ isActive }) => cn("sidebar-link", isActive && "active")}
                                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                            >
                                <item.icon className="h-5 w-5" />
                                <span>{item.label}</span>
                            </NavLink>
                        ))}
                    </>
                )}
            </nav>

            {/* User Menu */}
            <div className="p-4 border-t border-border/60">
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="w-full justify-start" data-testid="user-menu">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center mr-3">
                                <span className="text-sm font-medium text-primary">
                                    {user?.name?.charAt(0).toUpperCase()}
                                </span>
                            </div>
                            <div className="flex-1 text-left">
                                <p className="text-sm font-medium truncate">{user?.name}</p>
                                <p className="text-xs text-muted-foreground capitalize">{user?.role?.replace('_', ' ')}</p>
                            </div>
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                        <DropdownMenuItem onClick={() => navigate('/settings')} className="cursor-pointer">
                            <Settings className="h-4 w-4 mr-2" />
                            Settings
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-destructive" data-testid="logout-btn">
                            <LogOut className="h-4 w-4 mr-2" />
                            Logout
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
            </aside>
        </>
    );
};
